import { track } from "$lib/track";
import { browser } from "$app/environment";
import type { Order } from "types/backend/models/checkout/order.entity";
import type { OrderItem } from "types/backend/models/checkout/order-item.entity";
import type { ICartDetails } from "types/backend/models/checkout/cart.entity";
import type { IBaseCartItem } from "types/backend/models/checkout/base-cart-item.entity";
const IS_DEV = ["development", "staging"].includes(process.env.NODE_ENV || "");
if (IS_DEV && browser) {
  window.klaviyo = false;
}

export function setKlaviyo() {
  if (!window.klaviyo && !IS_DEV) {
    window._klOnsite = window._klOnsite || [];
    try {
      window.klaviyo = new Proxy(
        {},
        {
          get: function (n, i) {
            return i === "push"
              ? function () {
                  var n;
                  (n = window._klOnsite).push.apply(n, arguments);
                }
              : function (...args: any[]) {
                  const callback = typeof args[args.length - 1] === "function" ? args.pop() : undefined;
                  return new Promise(resolve => {
                    window._klOnsite.push([
                      i.toString(),
                      ...args,
                      function (result: any) {
                        if (callback) callback(result);
                        resolve(result);
                      },
                    ]);
                  });
                };
          },
        }
      );
    } catch (error) {
      if (!Array.isArray(window.klaviyo)) {
        window.klaviyo = [];
      }
      window.klaviyo.push = (...args: any[]) => window._klOnsite?.push(...args);
    }
  }
}

export enum ProductType {
  Ticket = "ticket",
  Hotel = "hotel",
  Fixture = "fixture",
}

export async function klaviyoIdentify(email?: string) {
  try {
    if (!klaviyo || !email || IS_DEV) {
      return false;
    }
    return klaviyo.identify({ $email: email });
  } catch (error) {
    console.error(error);
  }
}

export function klaviyoViewedItemTracker(item: IKlaviyoItem) {
  try {
    if (!klaviyo || IS_DEV) {
      return false;
    }
    //important tracking bits
    klaviyo.push(["track", "Viewed Product", item]);
    klaviyo.push([
      "trackViewedItem",
      {
        Title: item.ProductName,
        ItemId: item.ProductID,
        Categories: item.Categories,
        ImageUrl: item.ImageURL,
      },
    ]);
  } catch (error) {
    console.error(error);
  }
}

export function klaviyoAddToCartTracker(item: IKlaviyoAddToCart) {
  try {
    if (!klaviyo || IS_DEV) {
      return false;
    }
    return klaviyo.push(["track", "Added to Cart", item]);
  } catch (error) {
    console.error(error);
  }
}

export function klaviyoCartItems(items: IBaseCartItem[]) {
  return items.map(item => {
    const [service, _i18nuuid, _part1, _part2] = item.sku.split("-");
    if (service === "hotel") {
      return {
        ProductID: item.sku,
        ProductName: klaviyoCartItemSingleName(item),
        ItemPrice: item.total,
        Quantity: item.qty,
        RowTotal: item.total,
        Categories: [ProductType.Hotel],
      };
    }

    return {
      ProductID: item.sku,
      ProductName: klaviyoCartItemSingleName(item),
      Categories: [ProductType.Ticket],
      ItemPrice: item.total,
      Quantity: item.qty,
      RowTotal: item.total,
    };
  });
}

export function klaviyoCartItemNames(items: IBaseCartItem[]) {
  return items.map(item => {
    return klaviyoCartItemSingleName(item);
  });
}

export function klaviyoCartItemSingleName(item: IBaseCartItem) {
  const [service, _genProductId, _part1, _part2] = item.sku.split("-");
  if (service === "hotel") {
    return item.detail.hotelName;
  }
  return `${item?.event?.title || ""} ${item.detail?.ticketCategory?.title || ""}`.trim();
}

export function klaviyoStartCheckout(cart: ICartDetails) {
  try {
    if (!window.klaviyo || IS_DEV) {
      return false;
    }
    const klaviyoItem = klaviyoCart(cart);
    track("klaviyo", "start checkout", klaviyoItem);
    if (cart.items.length > 0) {
      klaviyo.push(["track", "Started Checkout", klaviyoItem]);
    }
  } catch (error) {
    track("klaviyo", "start checkout error", error);
    console.error(error);
  }
}

interface ICombinedOrder extends Order {
  items: OrderItem[];
}

export function klaviyoPlacedOrder(order: Order, orderItems: OrderItem[], attempts = 10) {
  try {
    if (!window.klaviyo || IS_DEV) {
      if (attempts > 0) {
        setTimeout(() => {
          klaviyoPlacedOrder(order, orderItems, attempts - 1);
        }, 100);
      }
      return false;
    }
    const orderWithItems = { ...order, items: orderItems } as ICombinedOrder;
    const klaviyoItem = klaviyoCart(orderWithItems, true, order.cartId);
    track("klaviyo", "Placing order", klaviyoItem);
    if (orderItems.length > 0 && order.customerEmail) {
      window.klaviyo.push(["track", "Placed Order", klaviyoItem]);
      track("klaviyo", "Placed order", klaviyoItem);
    }
  } catch (error) {
    track("klaviyo", "Place order error", (error as Error).message);
    console.error(error);
  }
}

function klaviyoAddress(cartAddress: any): IKlaviyoAddress {
  return {
    Company: "",
    Address1: cartAddress?.line1 || "",
    Address2: cartAddress?.line2 || "",
    City: cartAddress?.city || "",
    Region: "",
    RegionCode: "",
    Country: cartAddress?.country || "",
    CountryCode: cartAddress?.countryCode || "",
    Zip: cartAddress?.postcode || "",
  };
}

export function klaviyoIsIdentified() {
  return klaviyo.isIdentified();
}

function klaviyoCart(cart: ICombinedOrder, isOrder: true, altId: string): IKlaviyoCart;
function klaviyoCart(cart: ICartDetails, isOrder?: false): IKlaviyoCart;
function klaviyoCart(cart: ICartDetails | ICombinedOrder, isOrder = false, altId?: string): IKlaviyoCart {
  const Items = klaviyoCartItems(cart.items);
  const ItemNames = klaviyoCartItemNames(cart.items);
  const Categories: string[] = Items.reduce((categories: string[], item) => {
    return item.Categories.reduce((categories: string[], category: string) => {
      if (!categories.includes(category)) {
        return categories.concat(category);
      }
      return categories;
    }, categories);
  }, []);
  return {
    $unique_id: `${isOrder ? altId : cart._id}`,
    $value: isOrder ? (cart as Order).paidTotal : cart.total,
    Items,
    ItemNames,
    Categories,
    BillingAddress: {
      ...klaviyoAddress(cart.billingAddress),
      FirstName: cart.customerForename || "",
      LastName: cart.customerSurname || "",
      Phone: cart.customerTelephone || "",
    },
    ShippingAddress: {
      ...klaviyoAddress(cart.shippingAddress),
      FirstName: cart.customerForename || "",
      LastName: cart.customerSurname || "",
      Phone: cart.customerTelephone || "",
    },
  };
}

export interface IKlaviyoItem {
  ProductID: string;
  SKU?: string;
  ProductName: string;

  Quantity?: number;
  ItemPrice?: number; // include conversion rate if applicable, per unit
  RowTotal?: number; // include conversion rate if applicable, per unit

  ProductURL?: string;
  ImageURL?: string;
  Categories?: ProductType[];
}

export interface IKlaviyoCartItem {
  ProductID: string;
  SKU?: string;
  ProductName: string;
  Quantity: number;
  ItemPrice: number;
  RowTotal?: number;
}

export interface IKlaviyoAddToCart {
  $value?: number;
  AddedItemProductID: string;
  AddedItemProductName: string;
  AddedItemCategories?: ProductType[];
  AddedItemPrice: number;
  AddedItemQuantity: number;
  ItemNames: string[];
  Items: IKlaviyoCartItem[];
}

export interface IKlaviyoAddress {
  FirstName?: string;
  LastName?: string;
  Company: string;
  Address1: string;
  Address2: string;
  City: string;
  Region: string;
  RegionCode: string;
  Country: string;
  CountryCode: string;
  Zip: string;
  Phone?: string;
}

export interface IKlaviyoCart {
  $unique_id: string;
  $value?: number;
  Items: IKlaviyoCartItem[];
  ItemNames: string[];
  Categories: string[];
  BillingAddress: IKlaviyoAddress;
  ShippingAddress: IKlaviyoAddress;
}
